import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  from,
  fromError,
  fromPromise,
  Observable,
  NormalizedCacheObject,
  HttpLink,
  Operation,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createAsyncStorage } from '@react-native-async-storage/async-storage';
import * as Sentry from '@sentry/react-native';
import {
  MMKVWrapper,
  CachePersistor,
  AsyncStorageWrapper,
} from 'apollo3-cache-persist';
import fetch from 'cross-fetch';
import { print } from 'graphql';
import { createClient } from 'graphql-ws';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, AppState, Platform } from 'react-native';
import { createMMKV } from 'react-native-mmkv';

import {
  GetDraftMessageDocument,
  GetMeDocument,
  ListDraftDocumentsDocument,
  ListDraftProjectDocumentsDocument,
  ListDraftTaskDocumentsDocument,
  ListDraftMessagesDocument,
  typeDefs,
  Document,
  Message,
} from '@graphql/generated';
import i18n from '@i18n/i18n.config';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useNetInfo } from '@src/context/NetInfoContext';
import { useMixpanel } from '@src/utils/mixpanel';
import { clearStoragePreservingKeys } from '@utils/clearStoragePreservingKeys';
import { ErrorCode } from '@utils/errorCodes';
import {
  fetchWithTimeout,
  IS_NETWORK_ERROR_REGEX,
} from '@utils/fetchWithTimeout';
import { getApiConfig } from '@utils/getApiConfig';
import { getInvitedSignupPreservedKeys } from '@utils/invitedSignup';
// TA-10850: the missing-record shape of a 401. Shared with
// useTeamDetailFromQuery so the link and the UI agree on what not-found means.
import {
  hasUnrecognizedAuthErrorCode,
  isNotFoundGraphQLError,
  isResourceNotFoundError,
  NOT_FOUND_WORDING_OPERATIONS,
  unrecognizedAuthErrorCodes,
} from '@utils/isResourceNotFoundError';
import { emitProjectUpdated } from '@utils/projectUpdatedEvent';
import { IGNORED_ERROR_CODES } from '@utils/sentry/errorFilter';
import { notifyHandledGraphqlServerError } from '@utils/serverErrorNotice';
import { SecurityStorage, AsyncStorage } from '@utils/storage';

import { initializeCache } from './initializeCache';

type ApolloClientContext = {
  client: ApolloClient<NormalizedCacheObject> | undefined;
  clearCache?: () => Promise<void>;
  clearChatCache?: () => Promise<void>;
  // Object reference changes on every signal so React always triggers a
  // state update even when the code repeats (e.g. two consecutive 401s).
  recentStatusCode?: { code: number };
  clearRecentStatusCode?: () => void;
  // TA-10160 (QA round 2) / TA-10707: clears this session's
  // "the socket has completed a handshake" mark, which is what gates the
  // 4401 → logout signal. The provider outlives logout/login — it wraps
  // AuthProvider — so without this reset a second login in the same page
  // session would inherit the mark and read a startup-race 4401 as a revoked
  // session. Called from authContext.removeToken().
  resetWsConnectionState?: () => void;
  persistor?: CachePersistor<NormalizedCacheObject>;
};

const graphConfig = getApiConfig();

const CIRCUIT_BREAKER_THRESHOLD = 3;
const BASE_RETRY_DELAY_MS = 500;
const MAX_RETRY_JITTER_MS = 500;
const MAX_RETRY_DELAY_MS = 30_000;

const CACHE_MAX_SIZE = 5 * 1024 * 1024; // 5 MB
const CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const CACHE_LAST_PRUNED_KEY = 'apollo_cache_last_pruned';

// Mutations that must not retry more than once to avoid duplicate side-effects
const NON_IDEMPOTENT_MUTATIONS = new Set([
  'createMessageIndividual',
  'createDirectUploads',
  'addTaskFiles',
  'addProjectFiles',
]);

// Mutations the server resolves as a flip of the task's *current* state
// (`completeTask` completes an open task and reopens a completed one). A
// retry after a 5xx or dropped connection is not a repeat but a second flip:
// when the first attempt committed and only its response was lost (gateway
// timeout while Rails finishes), the retry quietly undoes it a few seconds
// later and the user ends up with the opposite of what they tapped
// (TA-11018 QA fail). Never auto-retried. Only useToggleCompleteTask
// re-reads the task after a failure; other completeTask call sites (My Tasks
// list, task modals) are left showing Apollo's rollback until their next
// refetch.
const TOGGLE_MUTATIONS = new Set(['completeTask']);

// Best-effort background reads (e.g. useToggleCompleteTask's post-failure
// re-read of a task) opt out of the generic server-error alert per call with
// `context: { suppressServerErrorNotice: true }`: the user has already been
// told about the failure that triggered them, and a second alert for the
// same tap adds nothing.
const isServerErrorNoticeSuppressed = (operation: Operation) =>
  !!operation.getContext()?.suppressServerErrorNotice;

const CONNECTION_ISSUE_EVENT = 'graphql-circuit-breaker';

// Cloudflare edge/origin error codes: 525 = SSL handshake failed, 530 = origin unreachable
const CLOUDFLARE_ERROR_CODES = new Set([525, 530]);
const CLOUDFLARE_ERROR_EVENT = 'graphql-cloudflare-error';

const RATE_LIMIT_EVENT = 'graphql-rate-limit';

const notifyCloudflareEdgeError = (onRetry: () => void) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CLOUDFLARE_ERROR_EVENT));
    return;
  }
  Alert.alert(i18n.t('shared:error'), i18n.t('shared:cloudflareEdgeErrorMsg'), [
    { text: 'Cancel', style: 'cancel' },
    { text: 'Retry', onPress: onRetry },
  ]);
};

const notifyCircuitBreakerTripped = () => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(CONNECTION_ISSUE_EVENT));
    return;
  }
  Alert.alert(i18n.t('shared:error'), i18n.t('shared:connectionIssueMsg'), [
    { text: 'OK' },
  ]);
};

// TA-10142: user-facing feedback for RATE_LIMIT_EXCEEDED GraphQL errors.
// Web dispatches a CustomEvent consumed by ServerErrorToast.web.tsx (mirrors
// the existing server-error/circuit-breaker/cloudflare event pattern above);
// native shows an Alert since there is no native toast host for this event.
const notifyRateLimitExceeded = () => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(RATE_LIMIT_EVENT));
    return;
  }
  Alert.alert(
    i18n.t('shared:error'),
    i18n.t('shared:errors.RATE_LIMIT_EXCEEDED'),
    [{ text: 'OK' }]
  );
};

const MUTATION_ERROR_EVENT = 'graphql-mutation-error';

// Shown once, right before the forced logout, when a 401 carries an error code
// this client does not recognize. The user is about to land on the login
// screen; without this they would read it as the app dropping them at random.
const ACCOUNT_ANOMALY_ERROR_KEY = 'accountAnomaly';

const notifyAccountAnomaly = () => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    // Same channel as notifyMutationBusinessError — ServerErrorToast.web
    // renders `errors.<errorCode>` for whatever code arrives.
    window.dispatchEvent(
      new CustomEvent(MUTATION_ERROR_EVENT, {
        detail: { errorCode: ACCOUNT_ANOMALY_ERROR_KEY },
      })
    );
    return;
  }
  Alert.alert(
    i18n.t('shared:error'),
    i18n.t(`shared:errors.${ACCOUNT_ANOMALY_ERROR_KEY}`),
    [{ text: 'OK' }]
  );
};

// Code review fix: an earlier version of this file excluded specific
// operationNames (createTask, updateMe, updateTask, assignTaskUsers,
// completeTask, assignTeamOwner, assignTaskOwner) from the global toast
// below, on the theory that they already show their own generic error UI
// locally. That was wrong — operationName-based exclusion is global to every
// call site of that mutation, and most of these operations have several
// other call sites with NO local error handling at all (e.g. completeTask in
// TasksListView.tsx, updateTask in TaskDetailForm.tsx, updateMe in
// RegisterForm.tsx/RegisterForm.web.tsx — confirmed via grep to have no
// onError/Alert of their own; RegisterForm's onError is console.log only,
// not user-visible). assignTeamOwner in TeamDetails.web.tsx only
// console.logs too. Excluding the operationName silenced the global toast
// for those call sites too, reproducing the exact silent-failure problem
// TA-10121 exists to fix. Fixing this properly would mean auditing every
// call site of every mutation (out of scope, previously and explicitly
// rejected for TA-10139).
// Instead: no exclusion list. Call sites that already have their own
// generic local error UI opt out per-call via
// `context: { suppressGlobalMutationErrorToast: true }` — currently
// TaskModal.tsx, TaskFormStack.tsx, AssignNewOwner.tsx, TeamMembers.web.tsx,
// NewEditProfile.web.tsx, MyTaskList.web.tsx, ChatAssignToTaskModal.web.tsx,
// MyTagModal.web.tsx, MyTasksNewItem.web.tsx, and the invite-link creation
// mutations in ChatLinkInviteModal.tsx/TaskLinkInviteModal.tsx/
// ProjectLinkInviteBottomSheet.tsx. (The initial audit for this list missed
// MyTasksNewItem.web.tsx, ChatAssignToTaskModal.web.tsx, and MyTagModal.web.tsx
// — a second code-review pass caught it; if you're adding a new mutation
// call site with its own local error UI, check here first.) Everywhere else
// will now show two error messages (local + global) for PERMISSION_DENIED/
// CONFLICT/SUBSCRIPTION_REQUIRED — a UX annoyance, not a functional bug, and
// a strictly better trade-off than the silent failures this list caused.

// TA-10121 Error Code Reference: PERMISSION_DENIED and CONFLICT are both
// generic ("Toast: No permission" / "Toast: prompt user to refresh") — unlike
// VALIDATION_FAILED, they don't need form-specific field mapping, so (like
// INTERNAL_ERROR/RATE_LIMIT_EXCEEDED above) they belong in the shared link
// rather than duplicated across every mutation call site (TA-10139).
const notifyMutationBusinessError = (errorCode: ErrorCode) => {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(MUTATION_ERROR_EVENT, { detail: { errorCode } })
    );
    return;
  }
  Alert.alert(i18n.t('shared:error'), i18n.t(`shared:errors.${errorCode}`), [
    { text: 'OK' },
  ]);
};

const isServerError = (
  networkError: unknown
): networkError is {
  statusCode?: number;
} => {
  if (!networkError || typeof networkError !== 'object') return false;
  const statusCode = (networkError as { statusCode?: number }).statusCode;
  return typeof statusCode === 'number' && statusCode >= 500;
};

// Returns true when a 401 network error carries GraphQL error extensions that
// indicate a permission/authorisation failure rather than a session failure.
// Apollo puts the response body in networkError.result for non-2xx responses,
// so graphQLErrors in the onError callback is empty for HTTP 401s.
// FORBIDDEN / PERMISSION_DENIED / NOT_AUTHORIZED mean the token is valid but
// the user lacks access to the resource — these must not trigger global logout.
export const isPermissionScopedError = (networkError: unknown): boolean => {
  const errors = (networkError as any)?.result?.errors as
    | { extensions?: { code?: string } }[]
    | undefined;
  return (
    errors?.some(
      (e) =>
        e?.extensions?.code === 'FORBIDDEN' ||
        e?.extensions?.code === ErrorCode.PERMISSION_DENIED ||
        e?.extensions?.code === 'NOT_AUTHORIZED'
    ) ?? false
  );
};

// A session failure hiding in a payload that also carries a permission error.
// `isPermissionScopedError` answers "any entry is permission-scoped", so
// without this a body of { FORBIDDEN, UNAUTHENTICATED } would take the
// permission shortcut and leave an expired session signed in (TA-10850 QA).
export const hasSessionFailureInErrors = (
  errors?: { extensions?: { code?: string } }[] | null
): boolean =>
  errors?.some(
    (e) =>
      e?.extensions?.code === ErrorCode.UNAUTHENTICATED ||
      e?.extensions?.code === 'UNAUTHORIZED'
  ) ?? false;

export const hasSessionFailureCode = (networkError: unknown): boolean => {
  const errors = (
    networkError as {
      result?: { errors?: { extensions?: { code?: string } }[] };
    } | null
  )?.result?.errors;
  return hasSessionFailureInErrors(errors);
};

// One account-anomaly notice per auth cycle. Every in-flight operation
// carrying the unknown code reaches the logout branch, and without this each
// one queues its own alert/toast for the same failure.
//
// Module-scoped, not per client: the provider keeps one client across
// logout/login, so a guard tied to the client lifetime would never reset and
// the second anomaly of an app session would log out with no explanation.
// Reset when a session actually starts — authContext calls
// `resetAccountAnomalyNotice` as the token becomes non-empty. Resetting at
// logout instead would fire for every late 401 from the dead session and let
// them notify again, and clearing the 401 signal is earlier still: removeToken
// is async (TA-10850 QA).
let hasNotifiedAccountAnomaly = false;

const notifyAccountAnomalyOnce = () => {
  if (hasNotifiedAccountAnomaly) return;
  hasNotifiedAccountAnomaly = true;
  notifyAccountAnomaly();
};

export const resetAccountAnomalyNotice = () => {
  hasNotifiedAccountAnomaly = false;
};

const apolloClientContext = createContext<ApolloClientContext | undefined>(
  undefined
);

export const getReplyMessage = (replyMessage?: Message) => {
  const attachments = replyMessage?.attachments?.map((document: any) => {
    const doc = document as Document;
    if (doc?.file?.url) return document;
    else {
      return {
        ...document,
        file: {
          ...doc?.file,
          url: doc?.file?.url ?? '',
          path: doc?.file?.path ?? '',
        },
      };
    }
  });
  if (!attachments) {
    return undefined;
  }
  // TA-11029: the MessageDetail fragments select replyMessage.deletedAt, but
  // the original is read from the cache through MessageDetailForCache, which
  // doesn't — writing the reply without it logs a missing field and leaves
  // listMessages reads incomplete (a bare page-1 refetch, TA-10379). An
  // original you can reply to isn't deleted, so null is its real value.
  return {
    ...replyMessage,
    deletedAt: replyMessage?.deletedAt ?? null,
    attachments: attachments,
  };
};

// TA-11196: anycable-go's close for a connection whose token it rejected.
// It sets the close code itself (1000, not 4401), so the reason is the only
// thing that tells it apart from a deploy restart or an ordinary close.
const ANYCABLE_AUTH_FAILED_REASON = 'Auth Failed';

export const isAnyCableAuthFailedClose = (event: unknown): boolean => {
  const closeEvent = event as { code?: number; reason?: string } | undefined;
  return (
    closeEvent?.code === 1000 &&
    closeEvent.reason === ANYCABLE_AUTH_FAILED_REASON
  );
};

export const setupApolloClient = async ({
  setPersistor,
  setRecentStatusCode,
  ws = true,
  netInfoRef,
  trackEvent,
  wsHasConnectedOnceRef,
}: {
  setPersistor?: (persistor: CachePersistor<NormalizedCacheObject>) => void;
  setRecentStatusCode?: (statusCode: number) => void;
  ws: boolean;
  // TA-10639 (dev review): a ref, not a snapshot. Setup runs once per provider
  // lifetime, so a captured `netInfo` value would freeze at `initialState` and
  // every Sentry event would report `unknown / connected` regardless of the
  // real connectivity at the time of the failure.
  netInfoRef?: React.MutableRefObject<any>;
  trackEvent: (eventName: string, properties?: Record<string, any>) => void;
  wsHasConnectedOnceRef?: React.MutableRefObject<boolean>;
}) => {
  // Circuit breaker state – counts consecutive 503 failures across operations
  let consecutive503Failures = 0;
  let circuitOpen = false;

  // Mutable ref so the errorLink closure can invoke client.refetchQueries once
  // newClient is assigned below (errorLink is constructed before the client).
  const retryRef: { fn: () => void } = { fn: () => {} };
  // Same late binding for the WS `on.closed` handler, which also has to reach
  // the client before it exists (TA-11196).
  const revalidateSessionRef: { fn: () => void } = { fn: () => {} };

  const cache = initializeCache();
  const storage =
    Platform.OS === 'web'
      ? new AsyncStorageWrapper(createAsyncStorage('tasktag_apollo'))
      : new MMKVWrapper(
          (() => {
            const m = createMMKV();
            return {
              getString: (key: string) => m.getString(key),
              set: (key: string, value: string | number | boolean) =>
                m.set(key, value),
              delete: (key: string) => m.remove(key),
              getAllKeys: () => m.getAllKeys(),
            };
          })()
        );

  const newPersistor = new CachePersistor({
    cache,
    storage,
    debug: process.env.NODE_ENV === 'dev' || __DEV__,
    trigger: 'background',
    maxSize: CACHE_MAX_SIZE,
  });

  setPersistor?.(newPersistor);

  const lastPruned = await AsyncStorage.getItem(CACHE_LAST_PRUNED_KEY);
  const now = Date.now();
  const lastPrunedAt =
    lastPruned === null ? Number.NaN : Number.parseInt(lastPruned, 10);
  const shouldPrune =
    lastPruned === null ||
    !Number.isFinite(lastPrunedAt) ||
    lastPrunedAt > now ||
    now - lastPrunedAt > CACHE_MAX_AGE_MS;

  if (shouldPrune) {
    await newPersistor.purge();
    await AsyncStorage.setItem(CACHE_LAST_PRUNED_KEY, String(now));
  }

  // await before instantiating ApolloClient, else queries might run before the cache is persisted
  await newPersistor.restore();
  cache.gc();

  // get the authentication token from local storage if it exists
  const getToken = async () => {
    try {
      const value = await SecurityStorage.getItem('authToken');

      if (value !== null) {
        return value;
      }
    } catch (e) {
      // error reading value
      console.log(e);
    }
  };

  // TA-10160: connectionParams race guard. Immediately after a fresh login,
  // SecurityStorage may not have finished persisting the auth token yet when
  // graphql-ws makes its first connection attempt (the same race already
  // guarded against on the HTTP 401 retry path below). Sending an empty
  // token in that window causes the backend to reject the handshake with
  // close code 4401, which graphql-ws then retries — surfacing as a
  // spurious "Disconnected" banner (and needlessly exercising the 4401→
  // logout signal) on an otherwise perfectly healthy new session. Re-read
  // the token once after a short delay before letting a connection attempt
  // go out with no token at all.
  const getTokenForWs = async () => {
    const token = await getToken();
    if (token) return token;
    await new Promise((resolve) => setTimeout(resolve, 300));
    return getToken();
  };

  const wsLink =
    ws &&
    new GraphQLWsLink(
      createClient({
        webSocketImpl: process.env.NODE_ENV === 'test' ? WebSocket : undefined,
        url: `${graphConfig.webSocketUrl}/graphql`,
        keepAlive: 10000,
        retryAttempts: 100,
        connectionParams: async () => ({ token: await getTokenForWs() }),
        shouldRetry: (e) => {
          if (!e?.code) return true;
          // TA-10842: no WebSocket-level rate-limit handling here on purpose.
          // The backend applies its throttle only at the HTTP controller layer
          // (login/signup/password) — the WS transport (AnyCable) emits no 429
          // and no 1013 "Try Again Later" close code, so there is nothing
          // deterministic to branch on. If a CDN ever starts throttling the
          // upgrade itself, it surfaces as an abnormal 1006 close that we
          // cannot distinguish from an ordinary dropped connection; special-
          // casing it would break legitimate reconnects. Handling that safely
          // needs the backend/edge to send a defined close code first.
          if (e.code >= 1000 && e.code <= 1013) return true;
          // TA-10117 (reopened): graphql-ws (v5.16.2) never actually calls
          // this function for a 4401 (Unauthorized) close — its internal
          // shouldRetryConnectOrThrow hardcodes Unauthorized (along with
          // BadRequest/BadResponse/InternalServerError/etc.) as immediately
          // fatal and throws before shouldRetry is consulted at all. An
          // earlier revision special-cased `e.code === 4401` here believing
          // it would make graphql-ws retry during the startup token race
          // (TA-10286) — that branch was unreachable dead code and never
          // had any effect. The startup race is instead covered by
          // getTokenForWs's own retry-read above (so the token is almost
          // always present by the time connectionParams() resolves); any
          // 4401 that still slips through is delivered to each active
          // subscription's own `error` handler (see
          // useProjectMemberAddedNotification, which resubscribes), and,
          // once the session is established, routed to logout below.
          return false;
        },
        on: {
          // Records the first successful handshake of this session, which is
          // the only thing the 4401 gate below needs. It writes a ref rather
          // than React state on purpose (TA-10707): nothing renders off socket
          // liveness any more — TA-10234 removed the "Disconnected" banner,
          // the sole consumer — and a `useState` here re-rendered every
          // `useApolloClient()` call site on each of up to `retryAttempts: 100`
          // reconnects, which is exactly the storm shape TA-10455/TA-10639
          // were about.
          connected: () => {
            // TA-11139: a reconnect may have missed projectUpdated payloads
            // while the socket was down, so tell the listeners (My Tasks) to
            // catch up once. The first connect of a session has missed nothing.
            if (wsHasConnectedOnceRef?.current) emitProjectUpdated();
            if (wsHasConnectedOnceRef) wsHasConnectedOnceRef.current = true;
          },
          closed: (event) => {
            // 4401 means the backend rejected the connectionParams token.
            // Only treat it as a logout signal if the WS has previously had a
            // successful handshake this session (wsHasConnectedOnceRef.current).
            // Before that, the socket is still in its startup sequence and a
            // 4401 is most likely a race between token storage and the first
            // connect attempt (mitigated by getTokenForWs above), not a
            // genuine session revocation. graphql-ws treats Unauthorized as
            // fatal and will NOT retry the connection itself either way —
            // recovery relies on each subscriber's own `error` handler
            // resubscribing (TA-10117 reopened).
            const sessionEstablished = wsHasConnectedOnceRef?.current ?? true;
            if (
              (event as { code?: number } | undefined)?.code === 4401 &&
              sessionEstablished
            ) {
              setRecentStatusCode?.(401);
            }
            // TA-11196 (TA-2593): anycable-go rejects a revoked token with a
            // plain 1000 "Auth Failed" close, not 4401 — which is what a
            // still-open tab sees after a password reset elsewhere closes its
            // socket and it reconnects. graphql-ws keeps retrying a 1000 while
            // any subscription is active, so on its own nothing would ever
            // send that tab to Log In. Ask the server instead of deciding
            // here: a WS close never logs out by itself, only a genuine HTTP
            // 401 from the revalidation does (TA-10286). Gated like the 4401
            // above, so a startup token race is not revalidated.
            if (isAnyCableAuthFailedClose(event) && sessionEstablished) {
              revalidateSessionRef.fn();
            }
          },
        },
      })
    );

  const httpLink = new HttpLink({
    uri: `${graphConfig.apiUrl}/graphql`,
    fetch: process.env.NODE_ENV === 'test' ? fetch : fetchWithTimeout,
  });

  const authLink = setContext(async (_foo, { headers }) => {
    const token = await getToken();

    return {
      headers: {
        ...headers,
        ...(token ? { authorization: `Bearer ${token}` } : {}),
      },
    };
  });

  // Measures the real HTTP body size by printing the DocumentNode to a GraphQL
  // string — matching what Apollo's HttpLink actually sends over the wire.
  // Scoped to the HTTP branch only so subscription operations (GraphQLWsLink)
  // are not measured — their wire format differs from the HTTP JSON body.
  const REQUEST_PAYLOAD_WARN_KB = 50;
  const requestSizeLink = new ApolloLink((operation, forward) => {
    const body = JSON.stringify({
      operationName: operation.operationName,
      variables: operation.variables,
      query: print(operation.query),
    });
    const sizeKB = new Blob([body]).size / 1024;
    if (sizeKB > REQUEST_PAYLOAD_WARN_KB) {
      if (__DEV__) {
        // eslint-disable-next-line no-console
        console.warn(
          `[Apollo] Large request payload: ${
            operation.operationName
          } is ${sizeKB.toFixed(1)}KB`
        );
      }
      Sentry.addBreadcrumb({
        category: 'apollo',
        message: `Large request payload: ${
          operation.operationName
        } (${sizeKB.toFixed(1)}KB)`,
        level: 'warning',
        data: { operationName: operation.operationName, sizeKB },
      });
    }
    return forward(operation);
  });

  // splitLink routes subscriptions to WebSocket and all other operations
  // (queries/mutations) to the HTTP branch. requestSizeLink is placed inside
  // the HTTP branch so it only measures operations that actually go over HTTP.
  const splitLink = wsLink
    ? split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wsLink,
        requestSizeLink.concat(httpLink)
      )
    : requestSizeLink.concat(httpLink);

  // Log any GraphQL errors or network error that occurred
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      // A caller-initiated abort (e.g. Activity Log's Try Again cancelling the
      // request it supersedes, TA-11017) is not an API error. Bail before any
      // of the handling below: it must not reach analytics, and the
      // non-server-error fallback would otherwise clear consecutive503Failures
      // and close the circuit, resetting a real 503 streak. RetryLink skips it
      // for the same reason.
      if (networkError?.name === 'AbortError') return;
      // Read through the ref on every error so the report reflects the
      // connectivity at failure time, not at client-setup time.
      const netInfo = netInfoRef?.current;
      // Only the team reads that render the unavailable screen may classify a
      // codeless 401 body as not-found by its wording (TA-10850 review).
      const wordingFallbackOption = {
        allowWordingFallback: NOT_FOUND_WORDING_OPERATIONS.has(
          operation.operationName ?? ''
        ),
      };
      const extraInfo: Record<string, any> = {
        operationName: operation.operationName,
        variables: operation.variables,
        platform: Platform.OS,
        environment: process.env.NODE_ENV,
        appState: AppState.currentState,
        timestamp: new Date().toISOString(),
        connectionInfo: {
          type: netInfo?.type || 'unknown',
          isConnected: netInfo?.isConnected ?? true,
          isInternetReachable: netInfo?.isInternetReachable ?? true,
          details: netInfo?.details || null,
        },
      };

      if (networkError) {
        extraInfo.networkError = {
          name: networkError.name,
          message: networkError.message,
          statusCode: networkError?.statusCode,
        };
        // 401 is a handled auth error (retry + logout path) — not a real API error
        if ((networkError as any)?.statusCode !== 401) {
          trackEvent(MIXPANEL_EVENTS.API_ERROR_ENCOUNTERED, {
            error_type: 'network',
            endpoint: operation.operationName,
            variables: operation.variables,
            message: networkError.message,
            status_code: (networkError as any)?.statusCode,
          });
        }

        if (isServerError(networkError)) {
          if (networkError.statusCode === 503) {
            // 503 counting and circuit-breaker management is handled in
            // retryLink.attempts so each individual request attempt is counted,
            // not just the final failure after all retries are exhausted.
          } else if (
            typeof networkError.statusCode === 'number' &&
            CLOUDFLARE_ERROR_CODES.has(networkError.statusCode)
          ) {
            // Cloudflare edge errors (525 SSL handshake, 530 origin unreachable)
            // are transient — offer a retry affordance for queries. Mutations get
            // the generic alert because refetchQueries cannot re-execute mutations
            // and retrying an unknown mutation action could confuse the user.
            consecutive503Failures = 0;
            circuitOpen = false;
            const opDefinition = getMainDefinition(operation.query);
            const isMutation =
              opDefinition.kind === 'OperationDefinition' &&
              opDefinition.operation === 'mutation';
            if (isMutation) {
              notifyHandledGraphqlServerError();
            } else {
              notifyCloudflareEdgeError(retryRef.fn);
            }
          } else {
            consecutive503Failures = 0;
            circuitOpen = false;
            // Search is non-critical — a server error during search should
            // not block the user with a modal. The backend converts most
            // database errors to GraphQL errors (no 5xx), but guard here
            // as defence-in-depth.
            if (
              operation.operationName !== 'search' &&
              !isServerErrorNoticeSuppressed(operation)
            ) {
              notifyHandledGraphqlServerError();
            }
          }
        } else if ((networkError as any)?.statusCode === 429) {
          // TA-10842: surface the backend's brute-force/rate-limit throttle
          // (e.g. AllowAuthAttemptsService on login) as a handled message
          // instead of letting it reach the user as a raw client error.
          // Reuses the existing RATE_LIMIT_EXCEEDED copy/toast (TA-10142) —
          // both mean "you're doing that too often, wait and retry".
          consecutive503Failures = 0;
          circuitOpen = false;
          notifyRateLimitExceeded();
        } else {
          consecutive503Failures = 0;
          circuitOpen = false;
        }
      }

      if (graphQLErrors) {
        extraInfo.graphQLErrors = graphQLErrors;
      }

      // TA-10138 bug fix: a single response can carry multiple GraphQL
      // errors with the same code (e.g. two resolvers both failing with
      // INTERNAL_ERROR). These flags make the user-facing notify calls
      // below fire at most once per response instead of once per error.
      let hasNotifiedServerError = false;
      let hasNotifiedRateLimit = false;
      let hasNotifiedMutationBusinessError = false;
      // Code review fix: a body-level UNAUTHENTICATED used to log out inline
      // from inside the loop below. That skipped both guards the HTTP-401
      // path relies on — the one-shot retry with a re-read token
      // (`_retried401`) and, for the WS, the `wsHasConnectedOnceRef` startup
      // gate — so a startup race between token storage and the first request
      // logged the user straight out. Collect it here and run it through the
      // same retry-then-logout path after the loop instead.
      let hasBodyUnauthenticated = false;
      let unauthenticatedRequestId: string | undefined;

      // TA-10139: PERMISSION_DENIED/CONFLICT are generic, mutation-scoped
      // codes (per the TA-10121 Error Code Reference) — only fire the
      // fallback toast for mutations, not queries/subscriptions, so a
      // background list refresh returning CONFLICT doesn't pop a toast.
      const mainDefinition = getMainDefinition(operation.query);
      const isMutationOperation =
        mainDefinition.kind === 'OperationDefinition' &&
        mainDefinition.operation === 'mutation';

      graphQLErrors?.forEach(({ message, locations, path, extensions }) => {
        const errorCode = extensions?.code as ErrorCode | undefined;
        const requestId = extensions?.request_id as string | undefined;

        // TA-10142: correlate this GraphQL error with the backend's Sentry
        // trace via request_id, regardless of which branch below (if any)
        // ends up suppressing/handling it.
        Sentry.addBreadcrumb({
          category: 'graphql',
          message: `GraphQL error: ${errorCode ?? 'UNKNOWN'}`,
          data: { requestId, operationName: operation.operationName },
          level: 'error',
        });

        const isDocumentsNotFoundError =
          message?.includes('Documents not found') &&
          path?.includes('documentsByClientIds');

        if (isDocumentsNotFoundError) {
          // eslint-disable-next-line no-console
          console.log(
            `[Expected Security Error]: Documents not found for documentsByClientIds operation (user lacks access to requested client IDs)`
          );
          return;
        }

        const isDuplicateMessageError =
          operation.operationName === 'createMessageIndividual' &&
          (message?.includes('Client has already been taken') ||
            extensions?.code === 'DUPLICATE_MESSAGE');

        if (isDuplicateMessageError) {
          return;
        }

        const isDeleteProjectFilesNotFound =
          operation.operationName === 'deleteProjectFiles' &&
          (errorCode === ErrorCode.RESOURCE_NOT_FOUND ||
            message?.toLowerCase().includes('could not be found'));

        if (isDeleteProjectFilesNotFound) {
          return;
        }

        // TA-10850: a not-found team on a 200 response. The screen renders
        // its own unavailable state from it, so it is a handled outcome —
        // reporting it as a generic server error would file Sentry/Mixpanel
        // noise for a stale link. Matches the network-level guard below,
        // which covers the HTTP 401 form of the same answer.
        //
        // Deliberately the strict, code-authoritative predicate — not the
        // 200 one the UI uses. The UI reads an unrecognized code by its
        // wording so a stale uuid does not sit on skeletons, but suppressing
        // telemetry is a stronger claim: an unfamiliar code stays reportable
        // even when the screen chooses to render the unavailable state.
        if (
          operation.operationName === 'getTeamDetail' &&
          isNotFoundGraphQLError({
            message,
            extensions: extensions as { code?: string } | undefined,
          })
        ) {
          return;
        }

        // TA-10138: standard error-code branches for codes not already
        // covered by network-level handling above (401/5xx). These are
        // additive — they do not replace the existing string-matching or
        // networkError.statusCode checks, which stay in place until backend
        // P01-P03 rollout is confirmed complete on every environment.
        // UNAUTHORIZED is the same session failure by another name, and the
        // 401 side already counts it as one — collecting only UNAUTHENTICATED
        // here let it bypass the retry/logout path entirely (TA-10850 QA).
        if (hasSessionFailureInErrors([{ extensions: { code: errorCode } }])) {
          // A GraphQL-body session failure arrives on a 200 response, so it
          // never reaches the network-level 401 branch below. Defer it to
          // that same retry-then-logout path (after this loop) rather than
          // logging out from here — see hasBodyUnauthenticated above.
          if (!hasBodyUnauthenticated) {
            hasBodyUnauthenticated = true;
            unauthenticatedRequestId = requestId;
          }
          return;
        }

        if (
          errorCode === ErrorCode.INTERNAL_ERROR ||
          errorCode === ErrorCode.SERVICE_UNAVAILABLE
        ) {
          // PR #5782 review fix: only INTERNAL_ERROR used to get a
          // Sentry.captureMessage — a GraphQL-body SERVICE_UNAVAILABLE
          // (unlike a network-level 503, which is tracked via
          // consecutive503Failures/circuit breaker above) was invisible to
          // monitoring, even though errorFilter.ts's own test suite asserts
          // SERVICE_UNAVAILABLE events should be retained, not dropped.
          Sentry.captureMessage(
            `GraphQL ${errorCode} on ${operation.operationName ?? 'unknown'}`,
            { level: 'error', extra: { requestId } }
          );
          if (
            !hasNotifiedServerError &&
            !isServerErrorNoticeSuppressed(operation)
          ) {
            hasNotifiedServerError = true;
            notifyHandledGraphqlServerError(requestId);
          }
          return;
        }

        if (errorCode === ErrorCode.RATE_LIMIT_EXCEEDED) {
          if (!hasNotifiedRateLimit) {
            hasNotifiedRateLimit = true;
            notifyRateLimitExceeded();
          }
          return;
        }

        // TA-10139: generic fallback for PERMISSION_DENIED/CONFLICT/
        // SUBSCRIPTION_REQUIRED on mutations. No per-operationName
        // exclusion — see the comment above OPERATIONS_WITH_GENERIC_LOCAL_FALLBACK's
        // removal for why: an exclusion keyed on operationName silences
        // every call site of that mutation, not just the ones with local
        // error UI. Instead, a call site that already shows its own generic
        // error message opts out per-call via
        // `context: { suppressGlobalMutationErrorToast: true }` (see call
        // sites for examples) — this only affects that specific call, not
        // other components calling the same mutation.
        if (
          isMutationOperation &&
          (errorCode === ErrorCode.PERMISSION_DENIED ||
            errorCode === ErrorCode.CONFLICT ||
            errorCode === ErrorCode.SUBSCRIPTION_REQUIRED)
        ) {
          const suppressGlobalMutationErrorToast =
            !!operation.getContext()?.suppressGlobalMutationErrorToast;
          if (
            !suppressGlobalMutationErrorToast &&
            !hasNotifiedMutationBusinessError
          ) {
            hasNotifiedMutationBusinessError = true;
            notifyMutationBusinessError(errorCode);
          }
          // Code review fix: this used to unconditionally `return` here,
          // which meant CONFLICT never reached the generic Sentry/Mixpanel
          // capture block below — but errorFilter.ts's IGNORED_ERROR_CODES
          // deliberately excludes CONFLICT (unlike PERMISSION_DENIED),
          // meaning the team wants CONFLICT frequency still monitored.
          // Falling through is safe: PERMISSION_DENIED/SUBSCRIPTION_REQUIRED
          // are in IGNORED_ERROR_CODES so isExpectedBusinessError still
          // skips them there; only CONFLICT actually reaches Sentry now.
        }

        // Backend TA-10050: messageDelete subscription occasionally returns a
        // Message stub with null createdAt. Downgrade to a Sentry warning and
        // suppress the exception — no user action needed.
        const isMessageDeleteNullCreatedAt =
          path?.[0] === 'messageDelete' &&
          message?.includes(
            'Cannot return null for non-nullable field Message.createdAt'
          );

        if (isMessageDeleteNullCreatedAt) {
          Sentry.captureMessage(
            `messageDelete null-createdAt (TA-10050): ${message}`,
            'warning'
          );
          return;
        }

        console.log(
          JSON.stringify(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          )
        );

        // Code review fix: errors whose code is in IGNORED_ERROR_CODES were
        // still being sent to Sentry/Mixpanel here, only to be dropped later
        // by errorFilter.ts's beforeSend — constructing the exception and
        // firing the Mixpanel event was pure waste for every expected
        // business error (e.g. every failed form validation). Skip both
        // calls up front instead of filtering after the fact.
        const isExpectedBusinessError =
          !!errorCode && IGNORED_ERROR_CODES.includes(errorCode);

        if (
          message &&
          message !== '' &&
          locations &&
          path &&
          !isExpectedBusinessError
        ) {
          Sentry.captureException(
            new Error(
              JSON.stringify(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
              )
            ),
            {
              tags: {
                operation: operation.operationName ?? 'unknown',
                // TA-10141: errorFilter.ts reads this tag to drop expected
                // business errors (RESOURCE_NOT_FOUND/VALIDATION_FAILED/
                // PERMISSION_DENIED) before they reach the Sentry dashboard.
                errorCode: errorCode ?? 'UNKNOWN',
              },
              extra: { ...extraInfo, requestId },
            }
          );
          trackEvent(MIXPANEL_EVENTS.API_ERROR_ENCOUNTERED, {
            error_type: 'graphql',
            endpoint: operation.operationName,
            variables: operation.variables,
            message,
            locations,
            path,
          });
        }

        const breakpoint = /\[(?<resource>.+?)\s(?<id>\d+)\] not found/;
        const errorEntityMatch = message.match(breakpoint);

        if (errorEntityMatch?.groups?.id) {
          const id = errorEntityMatch?.groups?.id;
          const __typename = errorEntityMatch?.groups?.resource;

          const identifiedResource = cache.identify({
            __typename,
            id,
          });
          cache.evict({ id: identifiedResource });
          cache.gc();
        }
      });
      if (hasBodyUnauthenticated) {
        const logoutForUnauthenticated = (
          reason: string,
          // Codes the retry answered with that this client cannot classify.
          // Without them the capture would file an unknown account state as a
          // plain UNAUTHENTICATED, which sends operators the wrong way.
          unknownCodes: string[] = []
        ) => {
          Sentry.captureMessage(
            `${
              unknownCodes.length
                ? 'Unrecognized error code'
                : 'UNAUTHENTICATED'
            } on ${operation.operationName ?? 'unknown'} — ${reason}, logging out`,
            {
              level: 'error',
              extra: {
                operationName: operation.operationName ?? 'unknown',
                requestId: unauthenticatedRequestId,
                codes: unknownCodes,
              },
            }
          );
          setRecentStatusCode?.(401);
        };

        const context = operation.getContext();
        if (context._retried401) {
          // Second body-level UNAUTHENTICATED for this operation — the token
          // really is expired or revoked.
          logoutForUnauthenticated('still unauthenticated after retry');
          return;
        }

        // Same race-condition guard as the HTTP-401 path below: re-read the
        // token (authLink re-reads it for the retry too, so a fresher token
        // stored in the meantime is picked up). Not a JWT refresh flow — the
        // backend issues long-lived ApiTokens with no refresh endpoint.
        return fromPromise(getToken().catch(() => undefined)).flatMap(
          (token) => {
            if (!token) {
              logoutForUnauthenticated('no stored token');
              return fromError(
                new Error(
                  `UNAUTHENTICATED on ${operation.operationName ?? 'unknown'}`
                )
              );
            }

            Sentry.addBreadcrumb({
              category: 'auth',
              message: `UNAUTHENTICATED on ${operation.operationName} — retrying with stored token`,
              level: 'info',
              data: { operationName: operation.operationName },
            });
            operation.setContext({ _retried401: true });

            // onError's forward() does not re-enter this handler, so the
            // retry's own body-level UNAUTHENTICATED has to be intercepted
            // here. It arrives on a successful (200) response, hence `next`
            // rather than `error` as in the HTTP-401 wrapper below.
            return new Observable((obs) => {
              const sub = forward(operation).subscribe({
                next: (result) => {
                  const stillUnauthenticated = hasSessionFailureInErrors(
                    result?.errors as
                      | { extensions?: { code?: string } }[]
                      | undefined
                  );
                  if (stillUnauthenticated) {
                    logoutForUnauthenticated(
                      'still unauthenticated after retry'
                    );
                  }
                  obs.next(result);
                },
                error: (retryErr) => {
                  // The retry can also come back as an HTTP 401 rather than a
                  // 200 carrying the code again — a body-level UNAUTHENTICATED
                  // must end in logout either way, or an expired session is
                  // left signed in (TA-10850 QA).
                  //
                  // Classified exactly like the HTTP-401 wrapper below, though:
                  // if the first response was an auth race and the retry
                  // answers "this team does not exist" — or a permission
                  // error — the session is fine, and logging out here would
                  // bury the unavailable screen the caller is about to render.
                  if (
                    (retryErr as { statusCode?: number })?.statusCode === 401 &&
                    (hasUnrecognizedAuthErrorCode(retryErr) ||
                      hasSessionFailureCode(retryErr) ||
                      (!isPermissionScopedError(retryErr) &&
                        !isResourceNotFoundError(
                          retryErr,
                          wordingFallbackOption
                        )))
                  ) {
                    // Same explanation the HTTP-401 paths give — this branch
                    // handles the same unknown-code condition, so it owes the
                    // user the same reason for the logout.
                    const retryUnknownCodes =
                      unrecognizedAuthErrorCodes(retryErr);
                    if (retryUnknownCodes.length) {
                      notifyAccountAnomalyOnce();
                    }
                    logoutForUnauthenticated(
                      retryUnknownCodes.length
                        ? 'retry answered with a code this client cannot classify'
                        : 'still unauthenticated after retry',
                      retryUnknownCodes
                    );
                  }
                  obs.error(retryErr);
                },
                complete: obs.complete.bind(obs),
              });
              return () => sub.unsubscribe();
            });
          }
        );
      }

      if ((networkError as any)?.statusCode === 401) {
        // An error code we do not recognize on a 401 describes an account
        // state this client cannot reason about. Retrying would send the same
        // token for the same unknown verdict, so log out immediately and say
        // why — silently dropping the user on the login screen reads as a bug.
        //
        // Checked before the permission and not-found shortcuts below, which
        // both answer for the payload as a whole: a mixed body such as
        // { FORBIDDEN, ACCOUNT_LOCKED } would otherwise return on its
        // permission entry and never reach this branch, leaving a session
        // active that this client has just been told it cannot reason about.
        if (hasUnrecognizedAuthErrorCode(networkError)) {
          Sentry.captureMessage(
            `401 on ${
              operation.operationName ?? 'unknown'
            } with an unrecognized error code — logging out`,
            {
              level: 'error',
              extra: {
                operationName: operation.operationName ?? 'unknown',
                codes: unrecognizedAuthErrorCodes(networkError),
              },
            }
          );
          notifyAccountAnomalyOnce();
          setRecentStatusCode?.(401);
          return fromError(networkError);
        }

        // Permission-scoped 401 (FORBIDDEN / PERMISSION_DENIED / NOT_AUTHORIZED):
        // the token is valid but the user lacks access to the resource. Surface
        // this as an operation-level error — do not retry, do not logout.
        //
        // Only when nothing in the payload says the session itself failed:
        // this check is "any entry is permission-scoped", so a body of
        // { FORBIDDEN, UNAUTHENTICATED } would otherwise return here and keep
        // an expired session alive.
        if (
          isPermissionScopedError(networkError) &&
          !hasSessionFailureCode(networkError)
        ) {
          return;
        }

        // Same reasoning for a not-found record (TA-10850): the session is
        // valid, so hand the error to the operation rather than retrying and
        // logging the user out.
        //
        // The wording fallback is scoped to the team reads that render the
        // unavailable screen: a codeless 401 on any other operation —
        // {"errors":[{"message":"User could not be found"}]} for a revoked
        // account — must keep going through retry-then-logout instead of
        // being handed to the caller with the dead session still signed in.
        if (isResourceNotFoundError(networkError, wordingFallbackOption)) {
          return;
        }

        const context = operation.getContext();
        if (!context._retried401) {
          // Race-condition guard: on app startup, SecureStore may return null for
          // an in-flight request that fired before the token finished loading.
          // We read the token again — authLink also re-reads it for the retry
          // request, so if a fresher token was stored in the meantime it is used.
          // If the retry also returns 401 (truly expired/revoked token), the
          // Observable error handler below calls setRecentStatusCode → logout.
          // Note: the backend issues long-lived ApiTokens with no refresh endpoint;
          // this is a race-condition guard, not a JWT token-refresh flow.
          return fromPromise(getToken().catch(() => undefined)).flatMap(
            (token) => {
              if (token) {
                Sentry.addBreadcrumb({
                  category: 'auth',
                  message: `401 on ${operation.operationName} — retrying with stored token`,
                  level: 'info',
                  data: { operationName: operation.operationName },
                });
                operation.setContext({ _retried401: true });
                // Wrap the retry so a second 401 also triggers logout.
                // onError's forward() does not re-enter this handler, so we
                // intercept the retry's error stream directly here.
                return new Observable((obs) => {
                  const sub = forward(operation).subscribe({
                    next: (result) => {
                      // The retry can answer 200 with a body-level
                      // UNAUTHENTICATED instead of another HTTP 401.
                      // forward() does not re-enter this link, so nothing
                      // else would notice, and the expired session would
                      // stay signed in (TA-10850 QA).
                      // UNAUTHORIZED counts too — the same session failure by
                      // another name, and hasSessionFailureCode already reads
                      // it that way on the 401 side.
                      const stillUnauthenticated = hasSessionFailureInErrors(
                        result?.errors as
                          | { extensions?: { code?: string } }[]
                          | undefined
                      );
                      if (stillUnauthenticated) {
                        Sentry.captureMessage(
                          `401 on ${
                            operation.operationName ?? 'unknown'
                          } — retry answered UNAUTHENTICATED, logging out`,
                          {
                            level: 'error',
                            extra: {
                              operationName:
                                operation.operationName ?? 'unknown',
                            },
                          }
                        );
                        setRecentStatusCode?.(401);
                      }
                      obs.next(result);
                    },
                    error: (retryErr) => {
                      // Unknown code first, same as the initial 401 path:
                      // isPermissionScopedError answers for the payload as a
                      // whole, so a retry body such as
                      // { FORBIDDEN, ACCOUNT_LOCKED } would otherwise keep the
                      // session alive on its permission entry.
                      const retryHasUnknownCode =
                        hasUnrecognizedAuthErrorCode(retryErr);
                      if (
                        (retryErr as any)?.statusCode === 401 &&
                        (retryHasUnknownCode ||
                          hasSessionFailureCode(retryErr) ||
                          (!isPermissionScopedError(retryErr) &&
                            !isResourceNotFoundError(
                              retryErr,
                              wordingFallbackOption
                            )))
                      ) {
                        Sentry.captureMessage(
                          `401 on ${
                            operation.operationName ?? 'unknown'
                          } after retry — ${
                            retryHasUnknownCode
                              ? 'unrecognized error code'
                              : 'token revoked or expired'
                          }, logging out`,
                          {
                            level: 'error',
                            extra: {
                              operationName:
                                operation.operationName ?? 'unknown',
                              // Empty unless the retry carried codes this
                              // client does not know — the reason the branch
                              // fired at all in that case.
                              codes: unrecognizedAuthErrorCodes(retryErr),
                            },
                          }
                        );
                        // Same explanation the initial path gives: without
                        // it an unrecognized code on the retry drops the user
                        // on the login screen with no reason.
                        if (retryHasUnknownCode) {
                          notifyAccountAnomalyOnce();
                        }
                        setRecentStatusCode?.(401);
                      }
                      obs.error(retryErr);
                    },
                    complete: obs.complete.bind(obs),
                  });
                  return () => sub.unsubscribe();
                });
              }
              Sentry.captureMessage(
                `401 on ${
                  operation.operationName ?? 'unknown'
                } — no token, logging out`,
                {
                  level: 'error',
                  extra: {
                    operationName: operation.operationName ?? 'unknown',
                  },
                }
              );
              setRecentStatusCode?.(401);
              return fromError(networkError);
            }
          );
        }
      }
    }
  );

  const retryLink = new RetryLink({
    attempts: (count, operation, error) => {
      if (!error) return false;
      // A caller-initiated abort (e.g. Activity Log's Try Again cancelling the
      // request it supersedes, TA-11017) is intentional: never retry it, and
      // keep it out of the 503 streak bookkeeping below. fetchWithTimeout
      // rethrows its own timeouts as TypeError, so AbortError is only ever a
      // caller cancel.
      if ((error as Error).name === 'AbortError') return false;
      // Circuit breaker: halt retries when consecutive 503s have tripped it
      if (circuitOpen) return false;

      const statusCode = (error as any).statusCode as number | undefined;
      const is503 = statusCode === 503;
      const isNetworkError = IS_NETWORK_ERROR_REGEX.test(error.message ?? '');
      const is5xx = typeof statusCode === 'number' && statusCode >= 500;
      const isCloudflareError =
        typeof statusCode === 'number' &&
        CLOUDFLARE_ERROR_CODES.has(statusCode);

      // Subscriptions travel through retryLink but their errors carry no HTTP
      // statusCode — skip breaker bookkeeping entirely so subscription failures
      // neither increment nor reset the 503 streak.
      const definition = getMainDefinition(operation.query);
      const isSubscription =
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription';
      if (isSubscription) return false;

      // Count every 503 request attempt (not just per-operation) so the circuit
      // opens after CIRCUIT_BREAKER_THRESHOLD consecutive 503 requests regardless
      // of how many operations they span. Non-503 errors reset the streak.
      if (is503) {
        consecutive503Failures++;
        if (consecutive503Failures >= CIRCUIT_BREAKER_THRESHOLD) {
          circuitOpen = true;
          notifyCircuitBreakerTripped();
          return false;
        }
      } else {
        consecutive503Failures = 0;
      }

      // Cloudflare edge errors (525/530) are transient infrastructure failures:
      // the server never received the request, so idempotency is not a concern.
      // Check before NON_IDEMPOTENT_MUTATIONS to give the full retry budget.
      if (isCloudflareError) return count < 5; // up to 4 retries
      if (TOGGLE_MUTATIONS.has(operation.operationName)) return false;
      // Non-idempotent mutations: at most 1 retry to avoid duplicate side-effects
      if (NON_IDEMPOTENT_MUTATIONS.has(operation.operationName)) {
        return count < 2;
      }

      if (is503) return count < 6; // up to 5 retries
      if (isNetworkError) return count < 4; // up to 3 retries
      if (is5xx) return count < 3; // up to 2 retries — non-Cloudflare 5xx
      return false;
    },
    delay: (count) => {
      const base = Math.min(
        BASE_RETRY_DELAY_MS * Math.pow(2, count),
        MAX_RETRY_DELAY_MS
      );
      // NOSONAR - retry-delay jitter, not security-sensitive; no crypto RNG needed
      const jitter = Math.random() * MAX_RETRY_JITTER_MS;
      return base + jitter;
    },
  });

  // Resets the circuit breaker whenever an HTTP query/mutation succeeds.
  // Subscriptions are excluded: their payloads arrive over WebSocket and do not
  // reflect HTTP service health, so they must not clear consecutive503Failures
  // between failing query/mutation attempts.
  const circuitResetLink = new ApolloLink((operation, forward) => {
    // Compute once per operation — not inside map — so high-frequency
    // subscription payloads don't repeatedly re-evaluate the definition.
    const definition = getMainDefinition(operation.query);
    const isSubscription =
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription';
    return forward(operation).map((result) => {
      if (!isSubscription) {
        consecutive503Failures = 0;
        circuitOpen = false;
      }
      return result;
    });
  });

  // Initialize Apollo Client
  // Note: errorPolicy is intentionally NOT set globally on watchQuery — each
  // query that needs partial-data error handling opts in explicitly.
  const newClient = new ApolloClient({
    cache,
    link: errorLink.concat(
      from([authLink, retryLink, circuitResetLink, splitLink])
    ),
    defaultOptions: {
      watchQuery: { fetchPolicy: 'cache-and-network' },
      mutate: { errorPolicy: 'all' },
    },
    resolvers: {
      Query: {
        listDraftDocuments: (_root, args, { cache }) => {
          const drafts = cache.readQuery({
            query: ListDraftDocumentsDocument,
            variables: {
              chatId: args.chatId,
            },
          });
          return drafts;
        },
        listDraftProjectDocuments: (_root, args, { cache }) => {
          const drafts = cache.readQuery({
            query: ListDraftProjectDocumentsDocument,
            variables: {
              projectId: args.projectId,
            },
          });
          return drafts;
        },
        listDraftTaskDocuments: (_root, args, { cache }) => {
          const drafts = cache.readQuery({
            query: ListDraftTaskDocumentsDocument,
            variables: {
              taskId: args.taskId,
            },
          });
          return drafts;
        },
        listDraftMessages: (_root, _args, { cache }) => {
          const drafts = cache.readQuery({
            query: ListDraftMessagesDocument,
          });
          if (!drafts) {
            // Fixes intermitent crashes during app reload and possibly other states
            return undefined;
          }
          return drafts;
        },
        getDraftMessage: (_root, args, { cache }) => {
          const draft = cache.readQuery({
            query: GetDraftMessageDocument,
            variables: {
              chatId: args.chatId,
            },
          });
          return draft;
        },
      },
      Mutation: {
        updateDraftMessage: (_root, args, { cache }) => {
          const {
            body,
            tagsCollection,
            localFiles,
            chatId,
            replyMessage,
            checkin,
          } = args.attributes;

          const replyMessage1 = getReplyMessage(replyMessage);

          const drafts = cache.writeQuery({
            query: GetDraftMessageDocument,
            data: {
              getDraftMessage: {
                __typename: 'DraftMessage',
                body: body || null,
                tagsCollection,
                localFiles,
                chatId,
                replyMessage: replyMessage1 || null,
                checkin: checkin || null,
              },
            },
            variables: { chatId: args.chatId },
          });
          return drafts;
        },
      },
    },
    typeDefs,
  });

  retryRef.fn = () => {
    newClient.refetchQueries({ include: 'active' }).catch(() => {});
  };

  // TA-11196: re-verify the session through the normal link chain, so the
  // errorLink alone decides — a bare 401 logs out, while a 200, a network
  // error, a 5xx or a permission-scoped 401 leaves the user signed in. Not
  // throttled like the focus revalidation (useRevalidateSessionOnFocus), since
  // this is a server signal; the in-flight guard keeps a burst of rejected
  // reconnects to one getMe at a time.
  let sessionRevalidationInFlight = false;
  revalidateSessionRef.fn = () => {
    if (sessionRevalidationInFlight) return;
    sessionRevalidationInFlight = true;
    getToken()
      .then((token) => {
        // Signed out already — nothing left to revalidate.
        if (!token) return;
        return newClient.query({
          query: GetMeDocument,
          fetchPolicy: 'network-only',
        });
      })
      .catch(() => undefined)
      .finally(() => {
        sessionRevalidationInFlight = false;
      });
  };

  return newClient;
};

export const ApolloClientProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = apolloClientContext;
  const { trackEvent } = useMixpanel();
  const trackEventRef = useRef(trackEvent);
  trackEventRef.current = trackEvent;
  const stableTrackEvent = useCallback(
    (eventName: string, properties?: Record<string, any>) =>
      trackEventRef.current(eventName, properties),
    []
  );
  const [loading, setLoading] = useState(false);
  const [client, setClient] = useState<
    ApolloClient<NormalizedCacheObject> | undefined
  >(undefined);
  const [persistor, setPersistor] =
    useState<CachePersistor<NormalizedCacheObject>>();
  const [recentStatusCode, setRecentStatusCodeState] = useState<
    { code: number } | undefined
  >();
  // TA-10160: whether the socket has completed at least one successful
  // handshake since this provider mounted. The initial connection sequence
  // can retry once or twice (e.g. an auth-token race right after a fresh
  // login) before landing its first `connected` event — that's normal
  // startup, not a revoked session — so the 4401 → logout gate gives it a
  // grace period until this flips true (TA-10286).
  //
  // A ref, not state (TA-10707). Its only reader is the `on.closed` gate
  // inside the setupApolloClient closure, which is built once and so has to
  // read it live anyway. It used to be mirrored into two published
  // `useState`s (`wsConnected`, `wsHasConnectedOnce`) for the "Disconnected"
  // banner; TA-10234 deleted that banner and left the state behind, still
  // re-rendering every `useApolloClient()` consumer on each socket event.
  const wsHasConnectedOnceRef = useRef(false);
  const clearRecentStatusCode = useCallback(
    () => setRecentStatusCodeState(undefined),
    []
  );
  const netInfo = useNetInfo();
  // Mirrors the live context value into a ref the errorLink closure can read.
  // The closure is built once, at client setup, so it must not capture the
  // value itself (TA-10639 dev review).
  const netInfoRef = useRef(netInfo);
  netInfoRef.current = netInfo;
  // TA-10160 (QA round 2): logout must clear the mark. It is scoped to this
  // provider's lifetime, which spans logout/login, so without this the next
  // login's first connection sequence loses its startup grace period and a
  // racing 4401 would log the user straight back out.
  const resetWsConnectionState = useCallback(() => {
    wsHasConnectedOnceRef.current = false;
  }, []);

  const clearChatCache = useCallback(async () => {
    const cache = client?.cache;
    if (!cache) return;

    cache.modify({
      id: 'ROOT_QUERY',
      fields(fieldValue, details) {
        const invalidateRegex = /(chat|messages)/g;
        if (invalidateRegex.test(details.fieldName.toLowerCase())) {
          return details.DELETE;
        }
        return fieldValue;
      },
    });
    const ids = cache.gc();
    if (__DEV__ && process.env.NODE_ENV !== 'test') {
      console.log('-=-after cache-=-', cache.extract());
      console.log({ idsRemoved: ids });
    }
  }, [client]);

  const clearCache = useCallback(async () => {
    if (Platform.OS === 'web') {
      const href = window.location.href;
      // These unauthenticated pages need in-flight GraphQL queries to complete.
      // Calling clearStore() here aborts them via AbortController.
      if (
        href?.includes('/project-report') ||
        href?.includes('/invitation-preview') ||
        href?.includes('/project-invitation-preview') ||
        href?.includes('/invited-user') ||
        href?.includes('/team-invitation-preview') ||
        // Only bypass for the unauthenticated shared-link URL (ends with /TeamDetails).
        // The general /my-account/subscriptionPlanDetail/ path is also used by
        // authenticated users; skipping clearStore() there would leave stale data
        // in memory after logout/401.
        (href?.includes('/my-account/subscriptionPlanDetail/') &&
          href?.includes('/TeamDetails'))
      ) {
        await persistor?.purge();
        return void 0;
      }
      await client?.clearStore();
      await persistor?.purge();
      // On web, SecurityStorage shares the same IndexedDB as AsyncStorage;
      // preserve session keys so the version-triggered cache clear doesn't log the user out.
      await clearStoragePreservingKeys(
        ['authToken', 'keepSignedIn'],
        AsyncStorage
      );
      return void 0;
    }
    await client?.clearStore();
    await persistor?.purge();
    // TA-10920: signup clears the cache when a different account was cached on
    // the device; keep the invited-signup markers and the pending invite so the
    // invite routing (skip owner onboarding, open the owner chat) still applies
    // to the new account, plus the per-account invited-member record.
    await clearStoragePreservingKeys(
      await getInvitedSignupPreservedKeys(),
      AsyncStorage
    );

    return void 0;
  }, [client, persistor]);

  /**
   * TA-10707 — audit follow-up to TA-10455 / TA-10639.
   *
   * This value used to be an inline object literal on the `<Provider>` below,
   * so every render of this component published a brand-new context object and
   * every one of the ~65 modules that call `useApolloClient()` re-rendered,
   * whether or not anything they read had changed.
   *
   * That is the amplifier behind the failure shape those two tickets share: an
   * external store pushing updates faster than React can settle. This provider
   * sits near the root and subscribes to two such stores — `useNetInfo()`
   * (see NetInfoContext's note on the `navigator.connection` storm) and the
   * graphql-ws socket, which can churn for up to `retryAttempts: 100`
   * reconnects on a flaky link. Either one turned a single connectivity event
   * into an app-wide re-render, which is what let consumer `setState`s pile up
   * into "Maximum update depth exceeded". (The socket's own contribution is
   * gone as of TA-10707 — its handlers now write a ref, not state — but the
   * 401 signal and the client/persistor handoff still render this provider.)
   *
   * Memoising restores React's bail-out: a connectivity event that leaves these
   * fields untouched now stops here. `clearCache`/`clearChatCache` are wrapped
   * in `useCallback` for the same reason — as plain function declarations they
   * were fresh on every render and would have defeated this memo.
   */
  const contextValue = useMemo(
    () => ({
      client,
      clearCache,
      clearChatCache,
      recentStatusCode,
      clearRecentStatusCode,
      resetWsConnectionState,
      persistor,
    }),
    [
      client,
      clearCache,
      clearChatCache,
      recentStatusCode,
      clearRecentStatusCode,
      resetWsConnectionState,
      persistor,
    ]
  );

  if (!client) {
    if (!loading) {
      setLoading(true);
      setupApolloClient({
        setPersistor,
        ws: true,
        // Wrap so each 401 signal creates a new object reference — even if the
        // code is the same, React sees a state change and the authContext effect fires.
        setRecentStatusCode: (code: number) =>
          setRecentStatusCodeState({ code }),
        netInfoRef,
        trackEvent: stableTrackEvent,
        wsHasConnectedOnceRef,
      }).then(setClient);
    }
    return null;
  }

  return (
    <Provider value={contextValue}>
      <ApolloProvider client={client}>{children}</ApolloProvider>
    </Provider>
  );
};

export const useApolloClient = (): ApolloClientContext => {
  const context = useContext(apolloClientContext);
  if (context === undefined) {
    throw new Error('useApolloClient must be used within a Provider');
  }
  return context;
};
